import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../config/settings-api';
import {
  slugifyFieldKey,
  normalizeNegativeOrderStatuses,
  getMainStatusOptionsForNegative,
  getStatusButtonStyles,
} from '../../utils/orderConfigHelpers';
import './Settings.css';
import './OrderSettings.css';

const serializeStatuses = (statuses) =>
  JSON.stringify(
    normalizeNegativeOrderStatuses(statuses || []).map((item) => ({
      label: item.label,
      value: item.value,
      color: item.color,
      availableIn: item.availableIn,
      subStatuses: item.subStatuses,
    }))
  );

const NegativeOrderStatusSettings = ({
  statuses = [],
  orderStatuses = [],
  canManage,
  onSaved,
}) => {
  const [rows, setRows] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNewStatus, setShowNewStatus] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [addingSubStatusIndex, setAddingSubStatusIndex] = useState(null);
  const [newSubLabel, setNewSubLabel] = useState('');

  const mainStatusOptions = useMemo(
    () => getMainStatusOptionsForNegative(orderStatuses, rows),
    [orderStatuses, rows]
  );

  const statusesSnapshot = useMemo(
    () => serializeStatuses(statuses),
    [statuses]
  );

  useEffect(() => {
    const normalized = normalizeNegativeOrderStatuses(statuses);
    setRows(normalized);
    setInitialSnapshot(statusesSnapshot);
  }, [statusesSnapshot, statuses]);

  const isChanged = serializeStatuses(rows) !== initialSnapshot;

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const handleAddStatus = () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error('Enter a status label');
      return;
    }
    const value = slugifyFieldKey(label);
    if (rows.some((row) => row.value === value)) {
      toast.error('This status already exists');
      return;
    }
    setRows((prev) => [
      ...prev,
      {
        label,
        value,
        color: '#991b1b',
        availableIn: [],
        subStatuses: [],
      },
    ]);
    setNewLabel('');
    setShowNewStatus(false);
  };

  const handleAddSubStatus = (rowIndex) => {
    const label = newSubLabel.trim();
    if (!label) {
      toast.error('Enter a sub-status label');
      return;
    }
    const value = slugifyFieldKey(label);
    const row = rows[rowIndex];
    if ((row.subStatuses || []).some((item) => item.value === value)) {
      toast.error('This sub-status already exists');
      return;
    }
    if (rows.some((item) => item.value === value)) {
      toast.error('This status value is already used');
      return;
    }
    updateRow(rowIndex, {
      subStatuses: [...(row.subStatuses || []), { label, value, color: row.color || '#991b1b' }],
    });
    setNewSubLabel('');
    setAddingSubStatusIndex(null);
  };

  const toggleAvailableIn = (rowIndex, statusValue) => {
    const row = rows[rowIndex];
    const current = row.availableIn || [];
    const next = current.includes(statusValue)
      ? current.filter((value) => value !== statusValue)
      : [...current, statusValue];
    updateRow(rowIndex, { availableIn: next });
  };

  const handleSave = async () => {
    if (!rows.length) {
      toast.error('At least one negative status is required');
      return;
    }
    setSaving(true);
    try {
      const payload = normalizeNegativeOrderStatuses(rows).map((row, index) => ({
        label: row.label.trim(),
        value: row.value || slugifyFieldKey(row.label),
        color: row.color || '#991b1b',
        position: index,
        availableIn: row.availableIn || [],
        subStatuses: (row.subStatuses || []).map((sub, subIndex) => ({
          label: sub.label.trim(),
          value: sub.value || slugifyFieldKey(sub.label),
          color: sub.color || row.color || '#991b1b',
          position: subIndex,
        })),
      }));

      const result = await settingsAPI.updateNegativeOrderStatuses(payload);
      if (result.success) {
        toast.success('Negative order statuses saved');
        setInitialSnapshot(serializeStatuses(payload));
        onSaved?.(payload);
      } else {
        toast.error(result.msg || result.error || 'Failed to save negative statuses');
      }
    } catch {
      toast.error('Failed to save negative statuses');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-card order-settings-card order-settings-card-negative">
      <div className="settings-card-header">
        <div className="settings-card-title">
          <AlertTriangle size={20} />
          <div>
            <h2>Negative Order Status</h2>
            <p>Cancelled, returned, and other exception flows</p>
          </div>
        </div>
        {canManage && (
          <button type="button" className="settings-save-btn" onClick={handleSave} disabled={!isChanged || saving}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>

      <div className="order-settings-body">
        <div className="order-negative-status-list">
          {rows.map((row, index) => (
            <div key={row.value || index} className="order-negative-status-card">
              <div className="order-status-row order-negative-status-main-row">
                <div className="order-status-color-input-row">
                  <input
                    type="color"
                    value={row.color || '#991b1b'}
                    disabled={!canManage}
                    onChange={(e) => updateRow(index, { color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="order-status-color-hex"
                    value={row.color || '#991b1b'}
                    disabled={!canManage}
                    placeholder="#991b1b"
                    onChange={(e) => updateRow(index, { color: e.target.value })}
                  />
                </div>
                <div className="order-status-main">
                  <input
                    type="text"
                    value={row.label}
                    disabled={!canManage}
                    onChange={(e) => updateRow(index, { label: e.target.value })}
                    onBlur={(e) => {
                      const label = e.target.value.trim();
                      updateRow(index, {
                        label: e.target.value,
                        value: slugifyFieldKey(label) || row.value,
                      });
                    }}
                  />
                  <span className="order-status-value">{slugifyFieldKey(row.label) || row.value}</span>
                </div>
                <span
                  className="order-status-preview order-status-preview-negative"
                  style={getStatusButtonStyles(row.color || '#991b1b')}
                >
                  {row.label}
                </span>
                {canManage && rows.length > 1 && (
                  <button
                    type="button"
                    className="order-settings-icon-btn danger"
                    onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                    title="Delete status"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="order-negative-status-divider" aria-hidden />

              <div className="order-negative-status-section">
                <span className="order-negative-status-section-label">Sub-statuses</span>
                <div className="order-negative-substatus-list">
                  {(row.subStatuses || []).map((sub, subIndex) => (
                    <div key={sub.value || subIndex} className="order-negative-substatus-row">
                      <div className="order-status-color-input-row order-negative-substatus-color-row">
                        <input
                          type="color"
                          value={sub.color || row.color || '#991b1b'}
                          disabled={!canManage}
                          onChange={(e) => {
                            const nextSubs = [...(row.subStatuses || [])];
                            nextSubs[subIndex] = { ...nextSubs[subIndex], color: e.target.value };
                            updateRow(index, { subStatuses: nextSubs });
                          }}
                        />
                        <input
                          type="text"
                          className="order-status-color-hex order-negative-substatus-color-hex"
                          value={sub.color || row.color || '#991b1b'}
                          disabled={!canManage}
                          placeholder="#991b1b"
                          onChange={(e) => {
                            const nextSubs = [...(row.subStatuses || [])];
                            nextSubs[subIndex] = { ...nextSubs[subIndex], color: e.target.value };
                            updateRow(index, { subStatuses: nextSubs });
                          }}
                        />
                      </div>
                      <div className="order-negative-substatus-main">
                        <input
                          type="text"
                          value={sub.label}
                          disabled={!canManage}
                          placeholder="Sub-status label"
                          onChange={(e) => {
                            const label = e.target.value;
                            const nextSubs = [...(row.subStatuses || [])];
                            nextSubs[subIndex] = { ...nextSubs[subIndex], label };
                            updateRow(index, { subStatuses: nextSubs });
                          }}
                          onBlur={(e) => {
                            const label = e.target.value.trim();
                            const nextSubs = [...(row.subStatuses || [])];
                            nextSubs[subIndex] = {
                              ...nextSubs[subIndex],
                              label: e.target.value,
                              value: slugifyFieldKey(label) || nextSubs[subIndex].value,
                            };
                            updateRow(index, { subStatuses: nextSubs });
                          }}
                        />
                        <span className="order-status-value">{slugifyFieldKey(sub.label) || sub.value}</span>
                      </div>
                      <span
                        className="order-status-preview order-status-preview-negative order-negative-substatus-preview"
                        style={getStatusButtonStyles(sub.color || row.color || '#991b1b')}
                      >
                        {sub.label || 'Preview'}
                      </span>
                      {canManage && (
                        <button
                          type="button"
                          className="order-settings-icon-btn danger"
                          onClick={() => {
                            updateRow(index, {
                              subStatuses: (row.subStatuses || []).filter((_, i) => i !== subIndex),
                            });
                          }}
                          title="Remove sub-status"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {canManage && (
                  <div className="order-settings-add-row no-margin">
                    {addingSubStatusIndex === index ? (
                      <div className="order-settings-new-field">
                        <input
                          type="text"
                          value={newSubLabel}
                          onChange={(e) => setNewSubLabel(e.target.value)}
                          placeholder="e.g. Return Reached"
                          onKeyDown={(e) => e.key === 'Enter' && handleAddSubStatus(index)}
                        />
                        <button type="button" className="settings-inline-btn" onClick={() => handleAddSubStatus(index)}>
                          Add
                        </button>
                        <button
                          type="button"
                          className="settings-inline-btn muted"
                          onClick={() => { setAddingSubStatusIndex(null); setNewSubLabel(''); }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="settings-inline-btn"
                        onClick={() => { setAddingSubStatusIndex(index); setNewSubLabel(''); }}
                      >
                        <Plus size={16} /> Add sub-status
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="order-negative-status-section">
                <span className="order-negative-status-section-label">Status available in</span>
                <div className="order-negative-available-in-grid">
                  {mainStatusOptions.map((option) => {
                    const selected = (row.availableIn || []).includes(option.value);
                    return (
                      <button
                        key={`${index}-${option.value}`}
                        type="button"
                        className={`order-negative-available-chip${selected ? ' selected' : ''}`}
                        disabled={!canManage}
                        onClick={() => toggleAvailableIn(index, option.value)}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        {canManage && (
          <div className="order-settings-add-row">
            {showNewStatus ? (
              <div className="order-settings-new-field">
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="e.g. Returned"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddStatus()}
                />
                <button type="button" className="settings-inline-btn" onClick={handleAddStatus}>Add</button>
                <button type="button" className="settings-inline-btn muted" onClick={() => { setShowNewStatus(false); setNewLabel(''); }}>Cancel</button>
              </div>
            ) : (
              <button type="button" className="settings-inline-btn" onClick={() => setShowNewStatus(true)}>
                <Plus size={16} /> Add negative status
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default NegativeOrderStatusSettings;
